/**
 * @fileoverview Compatibility adapter for artwork selection in shared admin forms.
 * @module ui-admin/components/IconPicker
 *
 * Responsibilities:
 * - Preserve the legacy IconPicker props and exports.
 * - Convert saved icon aliases to stable artwork IDs and emit selected artwork IDs.
 *
 * Dependencies:
 * - ui-artwork catalog and picker
 * - React
 *
 * Architecture:
 * - Presentation-only adapter over the shared artwork picker.
 *
 * Notes:
 * - Unknown legacy identifiers resolve to a bundled tag glyph instead of crashing.
 */
import React from 'react';
import { ArtworkPicker, ArtworkRenderer, getArtworkEntry } from '@firesquirrel/ui-artwork';

type IconComponentProps = {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * @contract IconPickerProps
 * Purpose: Preserve the legacy controlled icon-picker API for existing consumers.
 * Fields: value, onChange, placeholder, className.
 * Rules: Selected values and callback values are stable ui-artwork IDs; undefined clears the selection.
 * Sync: Presentation-only; persistence and synchronization remain with the caller.
 */
export interface IconPickerProps {
  value: string | undefined;
  onChange: (name: string | undefined) => void;
  placeholder?: string;
  className?: string;
}

/**
 * @process kebabToPascal
 * Steps: Capitalize the first character and each character following a hyphen.
 * Inputs: A legacy kebab-case icon identifier.
 * Outputs: A PascalCase identifier.
 * Failure Handling: Empty strings produce an empty string.
 * Idempotency: REQUIRED — repeated conversion yields the same identifier.
 * @reusable
 * Usage: Used by legacy consumers that build icon names from stored kebab-case values.
 * Rules: Pure string transformation; it does not resolve or validate artwork IDs.
 */
export function kebabToPascal(value: string): string {
  return value.replace(/(^|-)([a-z])/g, (_, _separator, character: string) => character.toUpperCase());
}

function resolveArtworkId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const resolved = getArtworkEntry(value)
    ?? getArtworkEntry(value.replace(/^gi-?/i, ''));
  return resolved?.id ?? 'glyph:tag';
}

/**
 * @process getIconComponent
 * Steps: Resolve the input through ui-artwork aliases, fall back to a tag glyph,
 *   and cache a renderer component for the resolved stable artwork ID.
 * Inputs: A legacy PascalCase, kebab-case, or artwork identifier.
 * Outputs: A React component that renders the matching artwork, or null when absent.
 * Failure Handling: Unknown non-empty identifiers render the safe bundled tag glyph.
 * Idempotency: REQUIRED — the same resolved ID returns the same component reference.
 * @reusable
 * Usage: Keeps non-lookup IconPicker consumers compatible with saved icon names.
 * Rules: Component generation is presentation-only and does not persist data.
 */
export function getIconComponent(value: string | undefined): React.ComponentType<IconComponentProps> | null {
  const artworkId = resolveArtworkId(value);
  if (!artworkId) return null;

  const cached = iconComponentsByArtworkId.get(artworkId);
  if (cached) return cached;
  const ArtworkIcon = function ArtworkIcon({ size = 24, className, style }: IconComponentProps) {
    return (
      <ArtworkRenderer
        artworkId={artworkId}
        color={style?.color}
        size={size}
        className={className}
      />
    );
  };
  iconComponentsByArtworkId.set(artworkId, ArtworkIcon);
  return ArtworkIcon;
}

const iconComponentsByArtworkId = new Map<string, React.ComponentType<IconComponentProps>>();

/**
 * @component IconPicker
 * Purpose: Preserve the legacy icon-picker API while authoring from the shared artwork catalog.
 * Props: Controlled icon value, string callback, optional search placeholder, and wrapper class.
 * Behavior: Resolves old aliases to artwork IDs and emits the selected canonical ID.
 * Data Source: Local bundled ui-artwork catalog; the caller owns persistence.
 * Constraints: Does not perform network or database access; unknown saved names use a tag glyph.
 * Performance: Delegates bounded catalog search and rendering to ArtworkPicker.
 * @reusable
 * Usage: Use for existing forms that still require the IconPicker export and prop contract.
 * Rules: Stateful catalog interaction is owned by ArtworkPicker; this adapter only translates values.
 */
export function IconPicker({ value, onChange, placeholder = 'Search icons...', className }: IconPickerProps) {
  const artworkId = resolveArtworkId(value);

  return (
    <div className={className}>
      <ArtworkPicker
        value={artworkId}
        placeholder={placeholder}
        onChange={({ artworkId: nextArtworkId }) => onChange(nextArtworkId)}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear icon selection"
          onClick={() => onChange(undefined)}
          className="mt-2 text-sm text-slate-600"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default IconPicker;
